/**
 * Platform-reserved root usernames for loamify.io/[username] vanity URLs.
 * Exact-match only after normalizeUsername(). Source of truth for claim checks.
 *
 * Categories: existing web-public routes, vision/PRD surfaces, brand, infra,
 * impersonation. Unethical handles live in unethical-usernames.ts.
 *
 * Root app URL segments live in WEB_PUBLIC_ROOT_ROUTE_SEGMENTS (shared with
 * web-public vanity shell detection). Claimable underscore/concat forms are
 * derived from hyphenated segments — do not maintain a second static list.
 */

/**
 * Actual first-path segments on loamify.io (Next.js app folders + auth-group URLs).
 * Used by web-public for vanity vs app-route detection and by claim reservations.
 */
export const WEB_PUBLIC_ROOT_ROUTE_SEGMENTS = [
  'a',
  'account',
  'bio',
  'bookmarks',
  'circles',
  'communities',
  'courses',
  'create',
  'creators',
  'discover',
  'drafts',
  'edit-profile',
  'events',
  'feed',
  'insights',
  'learning',
  'memberships',
  'messages',
  'notifications',
  'offers',
  'onboarding',
  'posts',
  'privacy',
  'products',
  'search',
  'settings',
  'stub',
  'terms',
  'cookies',
  'dmca',
  'u',
  'write-article',
  'login',
  'signup',
  'forgot-password',
  'reset-password',
  'become-creator',
  'verify-email',
] as const;

/** Auth/session words that are not folders today but must never be claimable. */
const AUTH_EXTRA_CLAIM_BLOCKLIST = [
  'logout',
  'signin',
  'signout',
  'register',
  'auth',
  'oauth',
  'sso',
  'callback',
  'session',
  'sessions',
  'password',
  'forgot',
  'reset',
  'verify',
] as const;

/** Expand a URL segment into claimable username forms (format: a-z0-9_). */
export function claimableFormsFromRouteSegment(segment: string): string[] {
  const forms = new Set<string>();
  const lower = segment.trim().toLowerCase();
  if (!lower) return [];
  if (/^[a-z0-9_]+$/.test(lower)) forms.add(lower);
  if (lower.includes('-')) {
    forms.add(lower.replace(/-/g, '_'));
    forms.add(lower.replace(/-/g, ''));
  }
  return [...forms];
}

const APP_ROUTE_USERNAMES = [
  ...WEB_PUBLIC_ROOT_ROUTE_SEGMENTS.flatMap((segment) => claimableFormsFromRouteSegment(segment)),
  ...AUTH_EXTRA_CLAIM_BLOCKLIST,
] as const;

/** Vision + social PRD product / nav / screen surfaces. */
const PRODUCT_SURFACE_USERNAMES = [
  'home',
  'explore',
  'discover',
  'discovery',
  'trending',
  'categories',
  'category',
  'tags',
  'tag',
  'topics',
  'topic',
  'articles',
  'article',
  'launches',
  'launch',
  'launchpad',
  'storefront',
  'store',
  'shop',
  'marketplace',
  'checkout',
  'cart',
  'orders',
  'order',
  'purchases',
  'purchase',
  'billing',
  'pricing',
  'plans',
  'plan',
  'subscription',
  'subscriptions',
  'upgrade',
  'premium',
  'events',
  'event',
  'mentorship',
  'mentor',
  'mentoring',
  'coaching',
  'coach',
  'newsletter',
  'newsletters',
  'circles',
  'circle',
  'community',
  'members',
  'member',
  'guests',
  'guest',
  'profile',
  'profiles',
  'me',
  'account',
  'accounts',
  'user',
  'users',
  'post',
  'comments',
  'comment',
  'replies',
  'reply',
  'likes',
  'like',
  'share',
  'shares',
  'bookmark',
  'follow',
  'following',
  'followers',
  'unfollow',
  'block',
  'blocks',
  'blocked',
  'mute',
  'mutes',
  'muted',
  'report',
  'reports',
  'moderation',
  'safety',
  'trust',
  'activity',
  'alerts',
  'inbox',
  'dm',
  'dms',
  'chat',
  'chats',
  'welcome',
  'getstarted',
  'get_started',
  'about',
  'help',
  'support',
  'contact',
  'careers',
  'jobs',
  'job',
  'blog',
  'news',
  'press',
  'legal',
  'tos',
  'cookies',
  'dmca',
  'copyright',
  'trademark',
  'security',
  'status',
  'health',
  'ads',
  'advertise',
  'advertising',
  'affiliate',
  'affiliates',
  'referral',
  'referrals',
  'invite',
  'invites',
  'invitation',
  'partners',
  'partner',
  'sponsorship',
  'sponsor',
  'media',
  'library',
  'analytics',
  'integrations',
  'integration',
  'domains',
  'domain',
  'custom_domain',
  'customdomain',
  'api',
  'apis',
  'docs',
  'documentation',
  'developers',
  'developer',
  'sdk',
  'graphql',
  'webhooks',
  'webhook',
  'ai',
  'automation',
  'assistant',
  'copilot',
  'feature_flags',
  'featureflags',
  'flags',
  'audit',
  'audit_logs',
  'auditlogs',
  'site_settings',
  'sitesettings',
  'dashboard',
  'studio',
  'admin',
  'moderator',
  'mods',
  'staff',
  'team',
  'official',
  'owner',
  'ownership',
  'ceo',
  'cto',
  'coo',
  'founder',
  'founders',
  'cofounder',
  'co_founder',
  'creator',
  'open_studio',
  'openstudio',
  'my_posts',
  'myposts',
  'my_products',
  'myproducts',
  'my_communities',
  'mycommunities',
  'my_courses',
  'mycourses',
  'my_events',
  'myevents',
  'my_mentorship',
  'mymentorship',
  'offers',
  'offer',
  'resources',
  'resource',
  'discussion',
  'discussions',
  'ask',
  'show',
  'story',
  'poll',
  'polls',
  'quests',
  'quest',
  'reputation',
  'badges',
  'badge',
  'verify_creator',
  'verified',
  'verification',
] as const;

/** Brand / platform identity. */
const BRAND_USERNAMES = [
  'loamify',
  'loam',
  'loamify_official',
  'official_loamify',
  'loamify_team',
  'loamify_support',
  'loamify_admin',
  'loamify_help',
  'loamify_founder',
  'thefounder',
  'team_loamify',
] as const;

/** Infra / technical / security. */
const INFRA_USERNAMES = [
  'www',
  'ftp',
  'mail',
  'email',
  'smtp',
  'root',
  'system',
  'null',
  'undefined',
  'true',
  'false',
  'assets',
  'static',
  'public',
  'private',
  'cdn',
  'img',
  'image',
  'images',
  'file',
  'files',
  'upload',
  'uploads',
  'download',
  'downloads',
  'robots',
  'sitemap',
  'favicon',
  'manifest',
  'next',
  '_next',
  'well_known',
  'wellknown',
  'nginx',
  'server',
  'backend',
  'frontend',
  'internal',
  'debug',
  'test',
  'testing',
  'staging',
  'prod',
  'production',
  'dev',
  'development',
  'localhost',
  'prometheus',
  'metrics',
  'rest',
  'v1',
  'v2',
  'websocket',
  'ws',
] as const;

/** Impersonation / trust abuse. */
const IMPERSONATION_USERNAMES = [
  'helps',
  'helpdesk',
  'customer_support',
  'customersupport',
  'abuse',
  'trust_safety',
  'trustsafety',
  'administrator',
  'sysadmin',
  'postmaster',
  'noreply',
  'no_reply',
  'billing_support',
  'billingsupport',
  'payments',
  'payment',
  'stripe',
  'paypal',
  'apple',
  'google',
  'meta',
  'instagram',
  'twitter',
  'facebook',
  'youtube',
  'tiktok',
  'linkedin',
] as const;

export const RESERVED_USERNAMES: ReadonlySet<string> = new Set<string>([
  ...APP_ROUTE_USERNAMES,
  ...PRODUCT_SURFACE_USERNAMES,
  ...BRAND_USERNAMES,
  ...INFRA_USERNAMES,
  ...IMPERSONATION_USERNAMES,
]);
